import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import { cn, formatIDR } from '@/lib/utils'
import type { TopProduct } from '@/types/api'
import { useQuery } from '@tanstack/react-query'
import { BarChart3 } from 'lucide-react'
import React, { useEffect, useState } from 'react'

interface TopProductsWidgetProps {
  className?: string
}

type MetricMode = 'revenue' | 'quantity'

export const TopProductsWidget: React.FC<TopProductsWidgetProps> = ({ className }) => {
  const [metric, setMetric] = useState<MetricMode>('revenue')
  const { toast } = useToast()

  const { data, isLoading, error } = useQuery({
    queryKey: ['topProducts', metric, 365],
    queryFn: () => apiClient.getTopProducts({ metric, period: 365, limit: 5 }),
  })

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to load top products',
        variant: 'destructive',
      })
    }
  }, [error, toast])

  const products = data?.data || []

  return (
    <Card className={cn('rounded-3xl border-slate-200/80 bg-white shadow-sm', className)}>
      <CardHeader className="border-b border-slate-100 pb-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-slate-950">Top Products</CardTitle>
            <CardDescription className="mt-1 text-sm text-slate-500">
              Best-performing products over the last 12 months.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1">
              {(['revenue', 'quantity'] as MetricMode[]).map((mode) => (
                <Button
                  key={mode}
                  variant="ghost"
                  size="sm"
                  onClick={() => setMetric(mode)}
                  className={cn(
                    'rounded-full px-4 text-xs font-semibold capitalize',
                    metric === mode
                      ? 'bg-slate-900 text-white hover:bg-slate-900 hover:text-white'
                      : 'text-slate-600 hover:bg-white hover:text-slate-900'
                  )}
                >
                  {mode}
                </Button>
              ))}
            </div>
            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-2.5 text-blue-700">
              <BarChart3 className="h-5 w-5" />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-20 rounded-2xl" />
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="space-y-3">
            {products.map((product: TopProduct, index) => {
              const value =
                metric === 'revenue'
                  ? formatIDR(product.total_revenue)
                  : `${product.total_quantity ?? product.total_sales} units`

              return (
                <div
                  key={product.id}
                  className="flex items-center gap-4 rounded-2xl border border-slate-200/80 px-4 py-3 transition-colors hover:bg-slate-50"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-sm font-semibold text-slate-700">
                    #{index + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-900">
                      {product.name}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      {product.category && <span>{product.category}</span>}
                      {product.order_count !== undefined && (
                        <span>{product.order_count} orders</span>
                      )}
                      {product.sku && <span>{product.sku}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-900">{value}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {metric === 'revenue'
                        ? `${product.total_quantity ?? 0} units sold`
                        : formatIDR(product.total_revenue)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 py-10 text-center text-sm text-slate-500">
            No top product data available for the selected period.
          </div>
        )}
      </CardContent>
    </Card>
  )
}
